import { h, Component, State, Prop, Host, Element, Watch, Event, EventEmitter } from '@stencil/core';

import { getCSSVariable } from '../../../../services/utils';

@Component({
  tag: 'pk-toast-item',
  styleUrl: './pk-toast-item.scss',
  shadow: true,
})
export class PkToastItem {
  // Own Properties
  // ====================

  private hasMessageSlot: boolean;
  private animationDelay: number = 600;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkToastItemElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() private animateFromBottom = true;

  // Public Property API
  // ========================

  /** Set the toast component appearance. */
  @Prop({ reflect: true }) appearance?: 'default' | 'success' | 'error' | 'warning' = 'default';

  /** Set a custom left border color (should be a valid token color). The icon (if present) will have the same color.
   * ⚠️ This property will override the appearance color!
   */
  @Prop({ mutable: true }) borderColor?: string;

  /** Set background color (should be a valid token color). */
  @Prop({ mutable: true }) bgColor?: string;

  /** If true, the toast will close (hide) automatically after the dismiss delay time (default: 3 seconds). */
  @Prop({ reflect: true }) autodismiss?: boolean = true;

  /** If true, the close icon will be shown at the right side of the component. */
  @Prop({ mutable: true }) closeButton?: boolean = true;

  /** Sets the amount of time when the toast component should close automatically. */
  @Prop({ mutable: true }) dismissDelay: number = 3000;

  // Prop lifecycle events
  // =======================

  @Watch('autodismiss')
  handleAutodismissPropChange() {
    if (!this.autodismiss) return;

    this.removeToast(this.dismissDelay);
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Emitted when the toast is dismissed (closed). */
  @Event() pkDismiss: EventEmitter<HTMLPkToastElement>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================
  componentWillLoad() {
    this.handleAutodismissPropChange();
  }

  componentDidLoad() {
    this.hasMessageSlot = !!this.el.querySelector('[slot="message"]');
    if (!this.hasMessageSlot) {
      console.warn(`[Pk-Toast-Item]: [slot="message"] is mandatory to be used.`);
    }
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // ==========================
  private removeToast = (delay: number = this.animationDelay) => {
    setTimeout(() => {
      this.animateFromBottom = false;
    }, delay - this.animationDelay);

    setTimeout(() => {
      this.el.remove();
      this.pkDismiss.emit(this.el);
    }, delay);
  };

  private handleOnClick = () => {
    this.removeToast();
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.borderColor && { '--toast--border-color': getCSSVariable(this.borderColor) }),
      ...(this.bgColor && { '--toast--bg-color': getCSSVariable(this.bgColor) }),
    };

    return (
      <Host
        class={{
          'pk-toast-item': true,
          'place-bottom': this.animateFromBottom,
          success: this.appearance === 'success',
          error: this.appearance === 'error',
          warning: this.appearance === 'warning',
          custom: !!this.borderColor,
        }}
        style={{ ...style }}
      >
        <div class="pk-toast-item__base flex flex-1" part="base">
          <div class="pk-toast-item__base--icon mr-s" part="icon">
            <slot name="icon" />
          </div>
          <div class="pk-toast-item__content flex flex-1 flex-col" part="content">
            <div class="pk-toast-item__content--title" part="title">
              <slot name="title" />
            </div>
            <div class="pk-toast-item__content--body" part="body">
              <slot name="body" />
            </div>
          </div>
          {this.closeButton && (
            <div class="pk-toast-item__close h-l w-l" part="close">
              <pk-button
                class="pk-toast-item__close--btn"
                part="close-button"
                appearance="icon"
                onClick={this.handleOnClick}
                width="24"
              >
                <pk-icon name="navigation-close" role="img" title="close" part="close-icon" />
                <span class="sr-only">Close</span>
              </pk-button>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
